/**
 * FLIP animation for lists that re-sort under the reader (the live Q&A, moderation queues).
 *
 * A polled list that snaps to a new order reads as a glitch and moves buttons out from under a
 * thumb mid-tap; animating rows to their new position keeps the reader's place. This hook is the
 * whole mechanism: attach the returned ref to the list container, stamp each row with
 * `data-flip-key`, and pass the data the order derives from as `deps`.
 *
 * How it works: after every render in `deps`, each keyed row's position is measured and compared
 * with where that key sat last time. A row that moved is started at its old position (the
 * "invert" of FLIP) and animated to rest — via the Web Animations API, so there is no transition
 * bookkeeping to unwind. Rows that did not move, and renders that reorder nothing, cost one
 * getBoundingClientRect each and no animation.
 *
 * Respects `prefers-reduced-motion`: measurement still runs (the map must stay current) but no
 * animation is created.
 */
import { useLayoutEffect, useRef } from "react";
/** Below this many pixels of movement the animation would read as jitter, not motion. */
const MIN_DISTANCE_PX = 2;
const DURATION_MS = 300;
export function useFlip(deps) {
    const containerRef = useRef(null);
    const lastPositions = useRef(new Map());
    useLayoutEffect(() => {
        const container = containerRef.current;
        if (!container)
            return;
        const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        const next = new Map();
        for (const row of container.querySelectorAll("[data-flip-key]")) {
            const key = row.dataset["flipKey"];
            if (!key)
                continue;
            const rect = row.getBoundingClientRect();
            next.set(key, { top: rect.top, left: rect.left });
            const before = lastPositions.current.get(key);
            if (reduced || !before)
                continue;
            const dx = before.left - rect.left;
            const dy = before.top - rect.top;
            if (Math.abs(dx) < MIN_DISTANCE_PX && Math.abs(dy) < MIN_DISTANCE_PX)
                continue;
            row.animate([{ transform: `translate(${dx}px, ${dy}px)` }, { transform: "translate(0, 0)" }], { duration: DURATION_MS, easing: "cubic-bezier(0.2, 0, 0.2, 1)" });
        }
        lastPositions.current = next;
        // The caller passes exactly the values the list order derives from.
    }, deps);
    return containerRef;
}
//# sourceMappingURL=use-flip.js.map